import { useEffect, useState } from "react";
import styled from "styled-components";
import { TrendingUp } from "lucide-react";
import { getClassDropMetricsApi } from "../../../api/apiEndpoints";
import { formatCurrency } from "../../../utils/partnerMetrics";

interface Metrics {
  dropsBooked: number;
  attended: number;
  noShow: number;
  lateCancelled: number;
  cancelledEarly: number;
  attendanceRate: number | null;
  newCustomersDelivered: number;
  studioRevenueGeneratedCents: number;
  membershipConversions: number;
  membershipConversionRate: number | null;
}

const RANGE_OPTIONS = [
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "Last 12 months", days: 365 },
];

const formatPct = (n: number | null) => (n === null ? "—" : `${Math.round(n * 100)}%`);
const formatUsd = (cents: number) => formatCurrency(cents / 100);

interface Props {
  businessId?: string;
}

const ClassDropsMetrics = ({ businessId }: Props) => {
  const [rangeDays, setRangeDays] = useState(30);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!businessId) return;
    setLoading(true);
    setError(null);
    const until = new Date();
    const since = new Date(until.getTime() - rangeDays * 24 * 60 * 60 * 1000);
    getClassDropMetricsApi(businessId, since.toISOString(), until.toISOString())
      .then((res) => {
        if (!cancelled) setMetrics(res.data);
      })
      .catch(() => !cancelled && setError("Could not load Class Drop metrics."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [businessId, rangeDays]);

  if (!businessId) return null;

  return (
    <Panel>
      <Header>
        <IconCircle><TrendingUp size={20} /></IconCircle>
        <div>
          <Title>Class Drops performance</Title>
          <Sub>What free classes are bringing your studio.</Sub>
        </div>
        <RangeRow>
          {RANGE_OPTIONS.map((opt) => (
            <RangeBtn key={opt.days} $active={rangeDays === opt.days} onClick={() => setRangeDays(opt.days)}>
              {opt.label}
            </RangeBtn>
          ))}
        </RangeRow>
      </Header>

      {loading ? (
        <Muted>Loading…</Muted>
      ) : error ? (
        <Muted>{error}</Muted>
      ) : !metrics ? null : (
        <>
          <Grid>
            <Card>
              <CardValue>{metrics.dropsBooked}</CardValue>
              <CardLabel>Class Drops Booked</CardLabel>
            </Card>
            <Card>
              <CardValue>{formatPct(metrics.attendanceRate)}</CardValue>
              <CardLabel>Attendance rate</CardLabel>
              <CardHint>{metrics.attended} attended of {metrics.attended + metrics.noShow + metrics.lateCancelled} enforceable</CardHint>
            </Card>
            <Card>
              <CardValue>{formatUsd(metrics.studioRevenueGeneratedCents)}</CardValue>
              <CardLabel>Fees passed to you</CardLabel>
              <CardHint>Late-cancel + no-show fees, in your next payout</CardHint>
            </Card>
          </Grid>

          <FooterRow>
            <FooterStat><FooterNum>{metrics.noShow}</FooterNum> no-shows</FooterStat>
            <FooterStat><FooterNum>{metrics.lateCancelled}</FooterNum> late cancels</FooterStat>
            <FooterStat><FooterNum>{metrics.cancelledEarly}</FooterNum> cancelled early</FooterStat>
          </FooterRow>
        </>
      )}
    </Panel>
  );
};

export default ClassDropsMetrics;

const Panel = styled.div`
  background: #ffffff;
  border: 1px solid #ece4da;
  border-radius: 16px;
  padding: 24px;
  font-family: 'Aileron', 'Poppins', sans-serif;
  margin-bottom: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f2efe9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9a8e84;
  flex: 0 0 auto;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #111111;
  margin: 0;
`;

const Sub = styled.p`
  font-size: 13.5px;
  color: #8a7f74;
  margin: 4px 0 0;
`;

const RangeRow = styled.div`
  display: flex;
  gap: 6px;
  margin-left: auto;
  flex: 0 0 auto;
`;

const RangeBtn = styled.button<{ $active: boolean }>`
  border: 1px solid ${(p) => (p.$active ? "#111111" : "#ece4da")};
  background: ${(p) => (p.$active ? "#111111" : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#4a4038")};
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 22px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.div`
  background: #faf8f4;
  border: 1px solid #ece4da;
  border-radius: 14px;
  padding: 16px;
`;

const CardValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #111111;
`;

const CardLabel = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: #4a4038;
  margin-top: 4px;
`;

const CardHint = styled.div`
  font-size: 11px;
  color: #9a8e84;
  margin-top: 6px;
  line-height: 1.4;
`;

const FooterRow = styled.div`
  display: flex;
  gap: 22px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #f2ede5;
  flex-wrap: wrap;
`;

const FooterStat = styled.div`
  font-size: 12.5px;
  color: #8a7f74;
`;

const FooterNum = styled.span`
  font-weight: 700;
  color: #111111;
`;

const Muted = styled.p`
  font-size: 13.5px;
  color: #8a7f74;
  line-height: 1.5;
  margin: 16px 0 0;
`;
